<template>
  <div class="po-detail">
    <KTCodePreview v-bind:title="'Chi tiết Đơn đặt xe'">
      <template v-slot:toolbar>
        <div class="po-toolbar d-flex flex-wrap align-items-center">
          <b-button
            class="mr-2 mb-2"
            size="sm"
            variant="secondary"
            @click="goBack"
          >
            <i class="flaticon2-back"></i> Quay lại
          </b-button>
          <b-button
            v-if="canApprove"
            class="mr-2 mb-2"
            size="sm"
            variant="success"
            @click="onApprove"
          >
            <i class="fas fa-check-circle"></i> Duyệt
          </b-button>
          <b-button
            v-if="canImportChassis"
            class="mr-2 mb-2"
            size="sm"
            variant="info"
            @click="openImportModal"
          >
            <i class="flaticon2-file"></i> Import số khung
          </b-button>
          <b-button
            v-if="canConfirmReceipt && pendingReceiptChassisIds.length"
            class="mr-2 mb-2"
            size="sm"
            variant="success"
            :disabled="!selectedChassisIds.length"
            @click="onConfirmReceipt"
          >
            <i class="fas fa-clipboard-check"></i>
            Xác nhận đã nhận (đã chọn {{ selectedChassisIds.length }})
          </b-button>
          <b-button
            v-if="canShortClose"
            class="mr-2 mb-2"
            size="sm"
            variant="warning"
            @click="onShortClose"
          >
            <i class="fas fa-door-closed"></i> Đóng ngắn
          </b-button>
          <b-button
            v-if="canReturnSupplier && returnableChassis.length"
            class="mr-2 mb-2"
            size="sm"
            variant="outline-danger"
            @click="openReturnModal"
          >
            <i class="fas fa-undo"></i> Trả NPP
          </b-button>
        </div>
      </template>

      <template v-slot:preview>
        <b-overlay :show="loading" rounded="sm">
          <div v-if="po">
            <!-- Header info -->
            <div class="po-section">
              <b-row>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Mã PO</div>
                  <div class="po-field-value mono">{{ po.code || '—' }}</div>
                </b-col>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Nhà phân phối</div>
                  <div class="po-field-value">{{ providerName }}</div>
                </b-col>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Trạng thái</div>
                  <span class="po-status-pill" :style="statusPillStyle">
                    <i :class="['po-status-icon', statusIconClass]"></i>
                    {{ statusLabel }}
                  </span>
                </b-col>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Tổng tiền</div>
                  <div class="po-field-value po-amount">
                    {{ formatPrice(po.totalAmount) }}
                  </div>
                </b-col>
              </b-row>
              <b-row class="mt-3">
                <b-col cols="12" md="3">
                  <div class="po-field-label">Ngày dự kiến giao</div>
                  <div class="po-field-value">
                    {{ formatDateOnly(po.expectedDeliveryDate) }}
                  </div>
                </b-col>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Hình thức thanh toán</div>
                  <div class="po-field-value">
                    {{ paymentMethodLabel(po.paymentMethod) }}
                  </div>
                </b-col>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Ngân hàng</div>
                  <div class="po-field-value">{{ po.bankName || '—' }}</div>
                </b-col>
                <b-col cols="12" md="3">
                  <div class="po-field-label">Số bảo lãnh</div>
                  <div class="po-field-value">
                    {{ po.guaranteeNumber || '—' }}
                  </div>
                </b-col>
                <b-col v-if="po.guaranteeDueDate" cols="12" md="3">
                  <div class="po-field-label">Ngày đáo hạn BL/phong tỏa</div>
                  <div class="po-field-value">
                    {{ formatDateOnly(po.guaranteeDueDate) }}
                  </div>
                </b-col>
              </b-row>
            </div>

            <!-- Items -->
            <div class="po-section">
              <h6 class="po-section-title">Danh sách xe đặt</h6>
              <table class="table table-bordered po-item-table">
                <thead>
                  <tr>
                    <th>Model / Màu xe</th>
                    <th>Kho</th>
                    <th>Người duyệt nhận</th>
                    <th class="text-center">SL đặt</th>
                    <th class="text-center">Đã nhập</th>
                    <th class="text-center">Tiến độ</th>
                    <th class="text-right">Giá nhập</th>
                    <th class="text-right">Thành tiền</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(it, idx) in items" :key="idx">
                    <td>
                      <div class="po-model-cell">
                        <span class="po-model-code mono">
                          {{ modelCode(it) }}
                        </span>
                        <span class="po-model-name">{{ modelName(it) }}</span>
                      </div>
                    </td>
                    <td>{{ itemStoreName(it) }}</td>
                    <td>{{ it.receiverName || '—' }}</td>
                    <td class="text-center">{{ it.quantityOrdered }}</td>
                    <td class="text-center">{{ it.quantityImported || 0 }}</td>
                    <td class="text-center">
                      <span class="po-progress">
                        {{ it.quantityImported || 0 }}/{{ it.quantityOrdered }}
                      </span>
                    </td>
                    <td class="text-right">
                      {{ formatPrice(it.importPrice) }}
                    </td>
                    <td class="text-right">{{ formatPrice(lineTotal(it)) }}</td>
                  </tr>
                  <tr v-if="!items.length">
                    <td colspan="8" class="text-center text-muted py-3">
                      Không có dòng nào.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <!-- Chassis (hàng đang về / đã về) -->
            <div class="po-section">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="po-section-title mb-0">Danh sách số khung</h6>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="loadChassis"
                >
                  <i class="flaticon2-refresh-1"></i> Tải lại
                </b-button>
              </div>
              <p v-if="hasPendingReceipt" class="po-confirm-hint text-muted">
                <i class="fas fa-info-circle po-confirm-hint-icon"></i>
                Khi xe về tới kho, tích chọn xe rồi bấm "Xác nhận đã nhận" để
                ghi công nợ + tồn kho. Chỉ người được phân công duyệt (cột
                "Người nhận") mới xác nhận được xe của mình.
              </p>
              <b-table
                :items="chassisRows"
                :fields="chassisFields"
                :busy="chassisLoading"
                responsive
                bordered
                small
                show-empty
                empty-text="Chưa có số khung nào (xe đang chờ về theo PO)."
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner small class="align-middle mr-2"></b-spinner>
                    <span>Đang tải...</span>
                  </div>
                </template>
                <template #head(select)>
                  <b-form-checkbox
                    v-if="canConfirmReceipt && pendingReceiptChassisIds.length"
                    :checked="allPendingSelected"
                    :indeterminate="somePendingSelected"
                    @change="toggleSelectAllPending"
                  />
                </template>
                <template #cell(select)="row">
                  <b-form-checkbox
                    v-if="
                      canConfirmReceipt &&
                      isVirtualStatus(row.item.status) &&
                      row.item.chassisId &&
                      canConfirmChassis(row.item)
                    "
                    :checked="selectedChassisIds.includes(row.item.chassisId)"
                    @change="toggleSelectChassis(row.item.chassisId)"
                  />
                </template>
                <template #cell(assignedReceiverName)="row">
                  <span v-if="row.item.assignedReceiverName">{{
                    row.item.assignedReceiverName
                  }}</span>
                  <span v-else class="po-engine-empty">—</span>
                </template>
                <template #cell(confirmedBy)="row">
                  <template v-if="row.item.confirmedByName">
                    <div>{{ row.item.confirmedByName }}</div>
                    <div
                      v-if="row.item.confirmedReceiptAt"
                      class="po-confirm-at"
                    >
                      {{ formatDateOnly(row.item.confirmedReceiptAt) }}
                    </div>
                  </template>
                  <span v-else class="po-engine-empty">—</span>
                </template>
                <template #cell(chassisNumber)="row">
                  <span class="mono">{{ row.item.chassisNumber }}</span>
                </template>
                <template #cell(engineNumber)="row">
                  <span
                    v-if="isEnginePlaceholder(row.item.engineNumber)"
                    class="po-engine-empty"
                  >
                    Chưa có
                  </span>
                  <span v-else class="mono">{{ row.item.engineNumber }}</span>
                </template>
                <template #cell(storeName)="row">
                  <span v-if="row.item.storeName">{{
                    row.item.storeName
                  }}</span>
                  <span v-else class="po-engine-empty">Chưa nhập kho</span>
                </template>
                <template #cell(status)="row">
                  <b-badge :variant="chassisStatusVariant(row.item.status)">
                    {{ chassisStatusLabel(row.item.status) }}
                  </b-badge>
                </template>
                <template #cell(expectedDeliveryDate)="row">
                  {{ formatDateOnly(row.item.expectedDeliveryDate) }}
                </template>
                <template #cell(actions)="row">
                  <b-button
                    v-if="canEditChassisRow(row.item)"
                    size="sm"
                    variant="link"
                    class="po-engine-btn p-0"
                    v-b-tooltip.hover
                    title="Sửa số khung / số máy"
                    @click="openChassisEditor(row.item)"
                  >
                    <i class="fas fa-pen po-engine-icon"></i>
                    <span class="po-edit-label">Sửa</span>
                  </b-button>
                </template>
              </b-table>
            </div>

            <!-- Lịch sử thao tác -->
            <div class="po-section">
              <div
                class="d-flex justify-content-between align-items-center mb-2"
              >
                <h6 class="po-section-title mb-0">Lịch sử thao tác</h6>
                <b-button
                  size="sm"
                  variant="outline-secondary"
                  @click="loadHistory"
                >
                  <i class="flaticon2-refresh-1"></i> Tải lại
                </b-button>
              </div>
              <b-table
                :items="historyRows"
                :fields="historyFields"
                :busy="historyLoading"
                responsive
                bordered
                small
                show-empty
                empty-text="Chưa có lịch sử."
              >
                <template #table-busy>
                  <div class="text-center text-primary my-2">
                    <b-spinner small class="align-middle mr-2"></b-spinner>
                    <span>Đang tải...</span>
                  </div>
                </template>
                <template #cell(createdAt)="row">
                  {{ formatDateTime(row.item.createdAt) }}
                </template>
                <template #cell(action)="row">
                  <b-badge :variant="historyVariant(row.item.action)">
                    {{ historyLabel(row.item.action) }}
                  </b-badge>
                </template>
                <template #cell(userName)="row">
                  {{ row.item.userName || '—' }}
                </template>
                <template #cell(detail)="row">
                  <span class="po-history-detail">{{
                    historySummary(row.item)
                  }}</span>
                </template>
              </b-table>
            </div>
          </div>
        </b-overlay>
      </template>
    </KTCodePreview>

    <!-- Modals -->
    <ImportChassisModal
      ref="importModal"
      :po-id="id"
      :items="items"
      @imported="onImported"
    />

    <!-- Modal: Đóng ngắn -->
    <b-modal
      v-model="shortCloseDialog.show"
      title="Đóng ngắn Đơn đặt xe"
      hide-footer
      no-close-on-backdrop
    >
      <p class="text-muted small mb-3">
        Giải phóng công nợ cam kết cho phần xe chưa nhập kho, chuyển PO sang
        Hoàn tất.
      </p>
      <b-form-group>
        <label>Lý do (không bắt buộc):</label>
        <b-form-textarea
          v-model="shortCloseDialog.reason"
          size="sm"
          :rows="3"
        />
      </b-form-group>
      <b-alert v-if="shortCloseDialog.error" variant="danger" show>
        {{ shortCloseDialog.error }}
      </b-alert>
      <div class="d-flex justify-content-end mt-3">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          @click="shortCloseDialog.show = false"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="warning"
          :disabled="shortCloseDialog.submitting"
          @click="submitShortClose"
        >
          Xác nhận
        </b-button>
      </div>
    </b-modal>

    <!-- Modal: Trả xe lỗi về NPP -->
    <b-modal
      v-model="returnDialog.show"
      title="Trả xe về Nhà phân phối"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <p class="text-muted small mb-2">
        Chọn xe (đã nhập kho, chưa bán) cần trả lại NPP. Hệ thống sẽ giảm công
        nợ phải trả, gỡ khỏi HĐ (nếu đang giữ) và xuất kho.
      </p>
      <table class="table table-bordered po-return-table">
        <thead>
          <tr>
            <th style="width: 40px" class="text-center">
              <b-form-checkbox
                :checked="allReturnSelected"
                :indeterminate="someReturnSelected"
                @change="toggleAllReturn"
              />
            </th>
            <th>Số khung</th>
            <th>Số máy</th>
            <th>Model</th>
            <th>Kho</th>
            <th class="text-center">Trạng thái</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="c in returnableChassis" :key="c.chassisId">
            <td class="text-center align-middle">
              <b-form-checkbox
                :checked="returnDialog.selected.includes(c.chassisId)"
                @change="toggleReturnSelect(c.chassisId)"
              />
            </td>
            <td class="align-middle mono">{{ c.chassisNumber }}</td>
            <td class="align-middle">
              <span
                v-if="isEnginePlaceholder(c.engineNumber)"
                class="po-engine-empty"
                >Chưa có</span
              >
              <span v-else class="mono">{{ c.engineNumber }}</span>
            </td>
            <td class="align-middle">{{ c.productName || '—' }}</td>
            <td class="align-middle">{{ c.storeName || '—' }}</td>
            <td class="text-center align-middle">
              <b-badge :variant="chassisStatusVariant(c.status)">
                {{ chassisStatusLabel(c.status) }}
              </b-badge>
            </td>
          </tr>
        </tbody>
      </table>
      <b-alert v-if="returnDialog.error" variant="danger" show class="py-2">
        {{ returnDialog.error }}
      </b-alert>
      <div class="d-flex justify-content-end mt-2">
        <b-button
          class="mr-2"
          size="sm"
          variant="secondary"
          @click="returnDialog.show = false"
        >
          Đóng
        </b-button>
        <b-button
          size="sm"
          variant="danger"
          :disabled="!returnDialog.selected.length || returnDialog.submitting"
          @click="submitReturn"
        >
          <b-spinner
            v-if="returnDialog.submitting"
            small
            class="mr-1"
          ></b-spinner>
          Trả NPP (đã chọn {{ returnDialog.selected.length }})
        </b-button>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { get } from 'lodash';
import moment from 'moment';

import KTCodePreview from '@/view/content/CodePreview.vue';
import PurchaseOrderService from '@/core/services/purchaseOrder.service';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import ImportChassisModal from '@/view/components/vehicle-purchase-orders/ImportChassisModal.vue';
import {
  PO_STATUS,
  PO_STATUS_LABEL,
  PO_STATUS_ICON,
  PO_STATUS_PILL,
  PO_PAYMENT_METHOD_LABEL,
  PO_CHASSIS_STATUS_LABEL,
  PO_CHASSIS_STATUS_VARIANT,
} from '@/core/constants/purchaseOrder';
import { checkPermission } from '@/utils/saveDataToLocal';
import { getUserProperty } from '@/utils/currentUser';
import { makeToastSuccess, makeToastFaile, convertPrice } from '@/utils/common';
import Swal from 'sweetalert2';

// Trạng thái xe ảo (chưa nhập kho) — xe ở các trạng thái này mới xác nhận nhận được.
const VIRTUAL_STATUSES = ['PO_ORDERED', 'ON_HOLDING_PENDING_IMPORT'];

export default {
  name: 'PurchaseOrderDetail',
  components: { KTCodePreview, ImportChassisModal },
  props: {
    id: { type: String, required: true },
  },
  data() {
    return {
      loading: false,
      po: null,
      chassisLoading: false,
      chassisRows: [],
      chassisFields: [
        {
          key: 'select',
          label: '',
          class: 'text-center',
          thStyle: { width: '40px' },
        },
        { key: 'chassisNumber', label: 'Số khung' },
        { key: 'engineNumber', label: 'Số máy' },
        { key: 'productName', label: 'Model xe' },
        { key: 'storeName', label: 'Kho' },
        { key: 'status', label: 'Trạng thái', class: 'text-center' },
        { key: 'assignedReceiverName', label: 'Người nhận (được giao)' },
        { key: 'confirmedBy', label: 'Người xác nhận' },
        {
          key: 'expectedDeliveryDate',
          label: 'Ngày dự kiến giao',
          class: 'text-center',
        },
        { key: 'actions', label: '', class: 'text-center' },
      ],
      // chassisId các xe PENDING_RECEIPT user đã tick để xác nhận.
      selectedChassisIds: [],
      historyLoading: false,
      historyRows: [],
      historyFields: [
        { key: 'createdAt', label: 'Thời gian' },
        { key: 'action', label: 'Hành động' },
        { key: 'userName', label: 'Người thực hiện' },
        { key: 'detail', label: 'Chi tiết' },
      ],
      shortCloseDialog: {
        show: false,
        reason: '',
        error: '',
        submitting: false,
      },
      returnDialog: {
        show: false,
        selected: [],
        error: '',
        submitting: false,
      },
    };
  },
  computed: {
    items() {
      return get(this.po, 'items') || [];
    },
    providerName() {
      return (
        get(this.po, 'provider.name') || get(this.po, 'providerName') || '—'
      );
    },
    statusLabel() {
      return (
        PO_STATUS_LABEL[get(this.po, 'status')] || get(this.po, 'status') || '—'
      );
    },
    statusIconClass() {
      return PO_STATUS_ICON[get(this.po, 'status')] || 'fas fa-circle';
    },
    statusPillStyle() {
      const info = PO_STATUS_PILL[get(this.po, 'status')] || {};
      return {
        background: info.bg || '#F3F6F9',
        color: info.color || '#80808F',
      };
    },
    isDraft() {
      return get(this.po, 'status') === PO_STATUS.DRAFT;
    },
    isApproved() {
      return get(this.po, 'status') === PO_STATUS.APPROVED;
    },
    canApprove() {
      return this.isDraft && checkPermission('PURCHASE_ORDER_APPROVE');
    },
    canShortClose() {
      return this.isApproved && checkPermission('PURCHASE_ORDER_CLOSE');
    },
    canImportChassis() {
      return (
        this.isApproved && checkPermission('PURCHASE_ORDER_IMPORT_CHASSIS')
      );
    },
    canConfirmReceipt() {
      return (
        this.isApproved && checkPermission('PURCHASE_ORDER_CONFIRM_RECEIPT')
      );
    },
    canReturnSupplier() {
      return checkPermission('PURCHASE_ORDER_RETURN_SUPPLIER');
    },
    // Xe đã nhập kho (NEW/ON_HOLDING), chưa bán → có thể trả lại NPP.
    returnableChassis() {
      return this.chassisRows.filter(
        (c) => ['NEW', 'ON_HOLDING'].includes(c.status) && c.chassisId,
      );
    },
    allReturnSelected() {
      const ids = this.returnableChassis.map((c) => c.chassisId);
      return (
        ids.length > 0 &&
        ids.every((id) => this.returnDialog.selected.includes(id))
      );
    },
    someReturnSelected() {
      const ids = this.returnableChassis.map((c) => c.chassisId);
      const n = ids.filter((id) =>
        this.returnDialog.selected.includes(id),
      ).length;
      return n > 0 && n < ids.length;
    },
    // User hiện tại (so với assignedReceiverBy để biết xe nào mình được giao nhận).
    currentUserId() {
      return getUserProperty('id') || null;
    },
    // Quyền xem/quản mọi kho → xác nhận hộ mọi xe (admin), bỏ qua ràng buộc phân công.
    canViewAllStore() {
      return checkPermission('STORE_VIEW_ALL');
    },
    // chassisId các xe ảo (chờ xác nhận nhận) mà user ĐƯỢC PHÉP xác nhận
    // (được phân công cho mình, hoặc có quyền storeViewAll).
    pendingReceiptChassisIds() {
      return this.chassisRows
        .filter(
          (c) =>
            VIRTUAL_STATUSES.includes(c.status) &&
            c.chassisId &&
            this.canConfirmChassis(c),
        )
        .map((c) => c.chassisId);
    },
    hasPendingReceipt() {
      return this.pendingReceiptChassisIds.length > 0;
    },
    allPendingSelected() {
      const ids = this.pendingReceiptChassisIds;
      return (
        ids.length > 0 &&
        ids.every((id) => this.selectedChassisIds.includes(id))
      );
    },
    somePendingSelected() {
      const ids = this.pendingReceiptChassisIds;
      const count = ids.filter((id) =>
        this.selectedChassisIds.includes(id),
      ).length;
      return count > 0 && count < ids.length;
    },
  },
  created() {
    this.loadDetail();
    this.loadChassis();
    this.loadHistory();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Đơn đặt xe', route: '/vehicle-purchase-orders' },
      { title: 'Chi tiết' },
    ]);
  },
  methods: {
    formatPrice(value) {
      if (value === null || value === undefined || value === '') return '-';
      return convertPrice(Number(value)) + ' đ';
    },
    formatDateOnly(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY') : '-';
    },
    formatDateTime(value) {
      if (!value) return '-';
      const m = moment(value);
      return m.isValid() ? m.format('DD/MM/YYYY HH:mm') : '-';
    },
    paymentMethodLabel(method) {
      return PO_PAYMENT_METHOD_LABEL[method] || (method ? method : '—');
    },
    historyLabel(action) {
      const MAP = {
        PO_CREATE: 'Tạo đơn',
        PO_UPDATE: 'Sửa đơn',
        PO_APPROVE: 'Duyệt',
        PO_CONFIRM_RECEIPT: 'Xác nhận đã nhận',
        PO_PAY_DEBT: 'Thanh toán công nợ',
        PO_SHORT_CLOSE: 'Đóng ngắn',
        PO_CANCEL: 'Hủy đơn',
        PO_RETURN_SUPPLIER: 'Trả NPP',
        PO_RECORD_ARRIVAL: 'Ghi nhận hàng về',
      };
      return MAP[action] || action;
    },
    historyVariant(action) {
      const MAP = {
        PO_CREATE: 'secondary',
        PO_UPDATE: 'info',
        PO_APPROVE: 'success',
        PO_CONFIRM_RECEIPT: 'success',
        PO_PAY_DEBT: 'primary',
        PO_SHORT_CLOSE: 'warning',
        PO_CANCEL: 'danger',
        PO_RETURN_SUPPLIER: 'danger',
      };
      return MAP[action] || 'light';
    },
    // Tóm tắt chi tiết 1 dòng lịch sử (đặc biệt PO_UPDATE: before→after).
    historySummary(item) {
      const a = item.action;
      const after = item.afterValue || {};
      const before = item.beforeValue || {};
      if (a === 'PO_CREATE') {
        return `Mã ${after.code || ''} · ${after.itemCount || 0} dòng · ${this.formatPrice(
          after.totalAmount,
        )}`;
      }
      if (a === 'PO_UPDATE') {
        const labels = {
          providerId: 'NPP',
          paymentMethod: 'Hình thức',
          bankName: 'Ngân hàng',
          guaranteeNumber: 'Số bảo lãnh',
          guaranteeDueDate: 'Ngày đáo hạn',
          paymentAmount: 'Số tiền',
          totalAmount: 'Tổng tiền',
          itemCount: 'Số dòng',
        };
        const parts = [];
        Object.keys(after).forEach((k) => {
          if (k === 'itemCount') return;
          const lbl = labels[k] || k;
          parts.push(
            `${lbl}: ${this.fmtVal(k, before[k])} → ${this.fmtVal(k, after[k])}`,
          );
        });
        if (after.itemCount !== undefined)
          parts.push(`Danh sách xe cập nhật (${after.itemCount} dòng)`);
        return parts.join('; ') || 'Cập nhật';
      }
      if (a === 'PO_PAY_DEBT')
        return `Trả ${this.formatPrice(after.amount)}${
          after.purchaseOrderId ? ' (gắn PO)' : ''
        }`;
      if (a === 'PO_CONFIRM_RECEIPT')
        return `Xác nhận ${(after.confirmedChassisIds || []).length} xe → ${after.purchaseOrderStatus || ''}`;
      if (a === 'PO_SHORT_CLOSE')
        return `Giải phóng ${this.formatPrice(after.releaseAmount)}`;
      if (a === 'PO_RETURN_SUPPLIER')
        return `Trả ${(after.returnedChassisIds || []).length} xe`;
      return '';
    },
    fmtVal(key, v) {
      if (v === null || v === undefined || v === '') return '(trống)';
      if (key === 'paymentMethod') return this.paymentMethodLabel(v);
      if (key === 'paymentAmount' || key === 'totalAmount')
        return this.formatPrice(v);
      if (key === 'guaranteeDueDate') return this.formatDateOnly(v);
      return String(v);
    },
    async loadHistory() {
      this.historyLoading = true;
      try {
        const res = await PurchaseOrderService.getHistory(this.id);
        const data = get(res, 'data.data') || get(res, 'data') || [];
        // Mới nhất lên đầu cho dễ xem.
        this.historyRows = Array.isArray(data) ? [...data].reverse() : [];
      } catch (err) {
        console.error('loadHistory error', err);
        this.historyRows = [];
      } finally {
        this.historyLoading = false;
      }
    },
    productLabel(it) {
      const name = get(it, 'product.name') || it.productName || '';
      const code = get(it, 'product.code') || it.productCode || '';
      if (code && name) return `${code} - ${name}`;
      return name || code || it.productId || '—';
    },
    modelCode(it) {
      return get(it, 'product.code') || it.productCode || it.productId || '—';
    },
    modelName(it) {
      return get(it, 'product.name') || it.productName || '';
    },
    // Tên kho của dòng (BE findOne trả relation item.store).
    itemStoreName(it) {
      return get(it, 'store.name') || it.storeName || '—';
    },
    lineTotal(it) {
      return (Number(it.quantityOrdered) || 0) * (Number(it.importPrice) || 0);
    },
    chassisStatusLabel(status) {
      return PO_CHASSIS_STATUS_LABEL[status] || status;
    },
    chassisStatusVariant(status) {
      return PO_CHASSIS_STATUS_VARIANT[status] || 'light';
    },
    // User được phép xác nhận xe này không? = có quyền mọi kho, hoặc chính
    // mình là người được phân công nhận (assignedReceiverBy). Khớp gate BE.
    canConfirmChassis(item) {
      if (this.canViewAllStore) return true;
      return (
        !!this.currentUserId && item.assignedReceiverBy === this.currentUserId
      );
    },
    // Số máy chưa có = rỗng hoặc placeholder toàn ký tự x/X ("xxxxxxxxxx").
    isEnginePlaceholder(engineNumber) {
      const v = (engineNumber || '').trim();
      return !v || /^x+$/i.test(v);
    },
    // Xe ảo PO_ORDERED: sửa được cả số khung + số máy. Status khác: chỉ bổ sung
    // số máy khi còn trống (số khung đã chốt khi ghép HĐ/nhập kho).
    canEditChassisRow(row) {
      if (!this.canImportChassis || !row.chassisId) return false;
      return (
        row.status === 'PO_ORDERED' ||
        this.isEnginePlaceholder(row.engineNumber)
      );
    },
    // 1 dialog gộp số khung + số máy. Số khung chỉ cho sửa khi PO_ORDERED;
    // số máy cho sửa khi PO_ORDERED hoặc đang trống. Gửi API cho từng trường đổi.
    async openChassisEditor(row) {
      const esc = (s) =>
        String(s == null ? '' : s).replace(
          /[&<>"]/g,
          (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c],
        );
      const chassisEditable = row.status === 'PO_ORDERED';
      const enginePlaceholder = this.isEnginePlaceholder(row.engineNumber);
      const curChassis = (row.chassisNumber || '').trim();
      const curEngine = enginePlaceholder
        ? ''
        : (row.engineNumber || '').trim();

      const lblStyle =
        'display:block;text-align:left;margin:10px 2px 2px;font-size:12.5px;font-weight:600;color:#475569;';
      const inputStyle =
        'margin:0;width:100%;font-family:Menlo,Consolas,monospace;';
      const chassisInput = chassisEditable
        ? `<input id="po-swal-chassis" class="swal2-input" style="${inputStyle}"
             value="${esc(curChassis)}" placeholder="Nhập số khung" />`
        : `<input class="swal2-input" style="${inputStyle}background:#f1f5f9;color:#94a3b8;"
             value="${esc(curChassis)}" disabled
             title="Đã ghép HĐ / nhập kho — không sửa được" />`;
      const chassisField = `<label style="${lblStyle}">Số khung${
        chassisEditable ? '' : ' (đã chốt)'
      }</label>${chassisInput}`;
      const engineField = `<label style="${lblStyle}">Số máy</label>
           <input id="po-swal-engine" class="swal2-input" style="${inputStyle}"
             value="${esc(curEngine)}" placeholder="${
               enginePlaceholder ? 'Chưa có — nhập số máy' : 'Nhập số máy'
             }" />`;

      const { value: result, isConfirmed } = await Swal.fire({
        title: 'Sửa số khung / số máy',
        html: `<div style="text-align:left;">${chassisField}${engineField}</div>`,
        focusConfirm: false,
        showCancelButton: true,
        confirmButtonText: 'Lưu',
        cancelButtonText: 'Hủy',
        preConfirm: () => {
          const chassisEl = document.getElementById('po-swal-chassis');
          const engineEl = document.getElementById('po-swal-engine');
          const chassisVal = chassisEl ? chassisEl.value.trim() : curChassis;
          const engineVal = engineEl ? engineEl.value.trim() : '';
          if (chassisEditable && !chassisVal) {
            Swal.showValidationMessage('Số khung không được để trống');
            return false;
          }
          if (!enginePlaceholder && !engineVal) {
            Swal.showValidationMessage('Số máy không được để trống');
            return false;
          }
          return { chassisVal, engineVal };
        },
      });
      if (!isConfirmed || !result) return;

      const chassisChanged =
        chassisEditable &&
        result.chassisVal &&
        result.chassisVal !== curChassis;
      const engineChanged = result.engineVal && result.engineVal !== curEngine;
      if (!chassisChanged && !engineChanged) return;

      try {
        if (chassisChanged) {
          await PurchaseOrderService.updateChassisNumber(
            row.chassisId,
            result.chassisVal,
          );
        }
        if (engineChanged) {
          await PurchaseOrderService.updateChassisEngine(
            row.chassisId,
            result.engineVal,
          );
        }
        makeToastSuccess('Đã cập nhật xe');
        this.loadChassis();
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Cập nhật thất bại',
        );
        // Có thể 1 trường đã lưu thành công → reload cho khớp DB.
        this.loadChassis();
      }
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await PurchaseOrderService.getById(this.id);
        this.po = get(res, 'data.data') || get(res, 'data') || null;
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Không tải được chi tiết',
        );
      } finally {
        this.loading = false;
      }
    },
    async loadChassis() {
      this.chassisLoading = true;
      try {
        // Toàn bộ chassis của PO (mọi trạng thái) + tên kho → quản lý xem được
        // xe đã về kho nào, số khung/số máy ra sao (không chỉ xe ảo như pipeline).
        const res = await PurchaseOrderService.getPoChassis(this.id);
        this.chassisRows = get(res, 'data.data') || get(res, 'data') || [];
        if (!Array.isArray(this.chassisRows)) this.chassisRows = [];
        // Bỏ các lựa chọn không còn PENDING_RECEIPT sau khi reload.
        const valid = new Set(this.pendingReceiptChassisIds);
        this.selectedChassisIds = this.selectedChassisIds.filter((id) =>
          valid.has(id),
        );
      } catch (err) {
        console.error('loadChassis error', err);
        this.chassisRows = [];
      } finally {
        this.chassisLoading = false;
      }
    },
    goBack() {
      this.$router.push({ name: 'list-purchase-order' });
    },
    async onApprove() {
      const result = await Swal.fire({
        title: 'Duyệt đơn đặt xe',
        text: `Duyệt đơn đặt xe ${this.po && this.po.code ? this.po.code : ''}? Công nợ cam kết của Nhà phân phối sẽ tăng tương ứng.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1bc5bd',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Duyệt',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
      });
      if (!result.value) return;
      try {
        await PurchaseOrderService.approve(this.id);
        makeToastSuccess('Đã duyệt đơn mua xe');
        this.loadDetail();
        this.loadHistory();
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') || err.message || 'Duyệt thất bại',
        );
      }
    },
    // Tick/bỏ tick 1 xe PENDING_RECEIPT.
    toggleSelectChassis(chassisId) {
      const idx = this.selectedChassisIds.indexOf(chassisId);
      if (idx === -1) this.selectedChassisIds.push(chassisId);
      else this.selectedChassisIds.splice(idx, 1);
    },
    // Chọn/bỏ chọn tất cả xe PENDING_RECEIPT (chỉ ảnh hưởng nhóm này).
    toggleSelectAllPending() {
      if (this.allPendingSelected) {
        this.selectedChassisIds = [];
      } else {
        this.selectedChassisIds = [...this.pendingReceiptChassisIds];
      }
    },
    // Bước 2 — xác nhận đã nhận: ghi công nợ thực + tồn kho cho các xe ĐÃ CHỌN.
    // BE chặn xe không thuộc kho user → lỗi hiển thị qua toast.
    async onConfirmReceipt() {
      const ids = [...this.selectedChassisIds];
      if (!ids.length) return;
      const result = await Swal.fire({
        title: 'Xác nhận đã nhận',
        text: `Xác nhận đã nhận ${ids.length} xe? Hệ thống sẽ ghi công nợ thực tế cho Nhà phân phối và cập nhật tồn kho.`,
        icon: 'question',
        showCancelButton: true,
        confirmButtonColor: '#1bc5bd',
        cancelButtonColor: '#E5EAEE',
        confirmButtonText: 'Xác nhận',
        cancelButtonText: '<span style="color:#464E5F">Hủy</span>',
        reverseButtons: true,
        showClass: { popup: 'animate__animated animate__fadeInDown' },
        hideClass: { popup: 'animate__animated animate__fadeOutUp' },
      });
      if (!result.value) return;
      try {
        await PurchaseOrderService.confirmReceipt(this.id, ids);
        makeToastSuccess(`Đã xác nhận nhận ${ids.length} xe`);
        this.selectedChassisIds = [];
        this.loadChassis();
        this.loadDetail();
        this.loadHistory();
      } catch (err) {
        makeToastFaile(
          get(err, 'response.data.message') ||
            err.message ||
            'Xác nhận thất bại',
        );
      }
    },
    onShortClose() {
      this.shortCloseDialog = {
        show: true,
        reason: '',
        error: '',
        submitting: false,
      };
    },
    async submitShortClose() {
      this.shortCloseDialog.submitting = true;
      this.shortCloseDialog.error = '';
      try {
        await PurchaseOrderService.shortClose(
          this.id,
          (this.shortCloseDialog.reason || '').trim() || undefined,
        );
        makeToastSuccess('Đã đóng ngắn đơn mua xe');
        this.shortCloseDialog.show = false;
        this.loadDetail();
        this.loadHistory();
      } catch (err) {
        this.shortCloseDialog.error =
          get(err, 'response.data.message') ||
          err.message ||
          'Đóng ngắn thất bại';
      } finally {
        this.shortCloseDialog.submitting = false;
      }
    },
    // Xe ảo (chưa nhập kho) — chỉ xe ở các trạng thái này mới xác nhận nhận được.
    isVirtualStatus(status) {
      return VIRTUAL_STATUSES.includes(status);
    },
    openReturnModal() {
      this.returnDialog = {
        show: true,
        selected: [],
        error: '',
        submitting: false,
      };
    },
    toggleReturnSelect(chassisId) {
      const idx = this.returnDialog.selected.indexOf(chassisId);
      if (idx === -1) this.returnDialog.selected.push(chassisId);
      else this.returnDialog.selected.splice(idx, 1);
    },
    toggleAllReturn() {
      if (this.allReturnSelected) {
        this.returnDialog.selected = [];
      } else {
        this.returnDialog.selected = this.returnableChassis.map(
          (c) => c.chassisId,
        );
      }
    },
    async submitReturn() {
      const ids = [...this.returnDialog.selected];
      if (!ids.length) return;
      this.returnDialog.submitting = true;
      this.returnDialog.error = '';
      try {
        await PurchaseOrderService.returnToSupplier(
          ids.map((chassisId) => ({ chassisId })),
        );
        makeToastSuccess(`Đã trả ${ids.length} xe về NPP`);
        this.returnDialog.show = false;
        this.loadChassis();
        this.loadDetail();
        this.loadHistory();
      } catch (err) {
        this.returnDialog.error =
          get(err, 'response.data.message') ||
          err.message ||
          'Trả NPP thất bại';
      } finally {
        this.returnDialog.submitting = false;
      }
    },
    openImportModal() {
      this.$refs.importModal.open();
    },
    onImported() {
      this.loadDetail();
      this.loadChassis();
    },
  },
};
</script>

<style lang="scss" scoped>
$po-border: #e5eaee;
$po-text-muted: #b5b5c3;
$po-amount: #2e7d32;

.po-detail {
  // Header card: title co theo nội dung, toolbar chiếm hết phần còn lại để cụm
  // nút nằm 1 hàng (mặc định .card-toolbar bị col-md-6 = 50% nên 4 nút wrap).
  ::v-deep .card-header {
    flex-wrap: nowrap;
    .card-title {
      flex: 0 0 auto;
      white-space: nowrap;
      .card-label,
      h3 {
        white-space: nowrap;
      }
    }
    .card-toolbar {
      flex: 1 1 auto;
      max-width: none;
    }
  }
  .po-toolbar {
    width: 100%;
    flex-wrap: nowrap;
    justify-content: flex-end;
    // Nút không co chữ; trên màn quá hẹp mới cho phép wrap để khỏi tràn.
    .btn {
      flex: 0 0 auto;
      white-space: nowrap;
    }
  }
  .po-section {
    background: #fff;
    border: 1px solid $po-border;
    border-radius: 6px;
    padding: 14px;
    margin-bottom: 14px;
  }
  .po-section-title {
    font-size: 13px;
    font-weight: 700;
    color: #181c32;
    margin-bottom: 12px;
  }
  .po-field-label {
    font-size: 11px;
    color: $po-text-muted;
    font-weight: 500;
    margin-bottom: 3px;
  }
  .po-field-value {
    font-size: 13px;
    font-weight: 600;
    color: #181c32;
  }
  .po-amount {
    color: $po-amount;
  }
  .mono {
    font-family: 'SF Mono', Menlo, Consolas, monospace;
    font-variant-numeric: tabular-nums;
    letter-spacing: 0.02em;
  }
  .po-progress {
    font-weight: 600;
  }
  .po-item-table th {
    background-color: #f3f6f9;
    font-size: 12px;
    vertical-align: middle;
  }

  .po-status-pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 100px;
    font-size: 11.5px;
    font-weight: 600;
    white-space: nowrap;
    line-height: 1;
  }
  .po-status-icon {
    font-size: 10px;
    color: inherit !important;
  }
  // Số máy chưa có
  .po-engine-empty {
    font-style: italic;
    color: #b5b5c3;
    font-size: 12px;
  }
  // Gợi ý xác nhận đa kho
  .po-confirm-hint {
    font-size: 11.5px;
    margin-bottom: 8px;
  }
  .po-confirm-hint-icon {
    margin-right: 3px;
    color: inherit !important;
  }
  // Ngày xác nhận (dòng phụ dưới tên người xác nhận)
  .po-confirm-at {
    font-size: 11px;
    color: #b5b5c3;
  }
  // Nút bổ sung số máy
  .po-engine-btn {
    color: #3699ff;
  }
  .po-engine-icon {
    font-size: 12px;
    color: inherit !important;
  }
  .po-edit-label {
    font-size: 11.5px;
    margin-left: 3px;
    white-space: nowrap;
  }
  // Cột Model: mã (mono, đậm) trên — tên dưới, 2 dòng cho dễ nhìn.
  .po-model-cell {
    display: flex;
    flex-direction: column;
    gap: 1px;
    .po-model-code {
      font-size: 12.5px;
      font-weight: 600;
      color: #3699ff;
    }
    .po-model-name {
      font-size: 12.5px;
      color: #3f4254;
    }
  }
}
</style>
